import React, { useState } from "react";
import styled from "styled-components";
import { FiClock, FiCalendar } from "react-icons/fi";
import {
    Header,
    Card,
    ButtonRow,
    PrimaryButton,
    SecondaryButton,
    Section,
    SectionLabel,
    SectionDesc,
} from "./styles/shared";
import EnableToggleCard from "./components/EnableToggleCard";
import DurationPicker from "./components/DurationPicker";

const QUICK_DAY_OPTIONS = [3, 7, 14, 30].map((d) => ({ label: `${d} days`, days: d }));

const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Deadlines count from the moment of submission, so the example needs a time of day.
const EXAMPLE_SUBMIT_TIME = new Date(2024, 10, 10, 13, 0); // Nov 10, 1:00 PM
const fmtWithTime = (d) =>
    `${fmt(d)} at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
const exampleWindowClose = (days) =>
    fmtWithTime(new Date(EXAMPLE_SUBMIT_TIME.getTime() + days * 24 * 60 * 60 * 1000));

const DeadlineDefaultSettings = ({
    sessions = [],
    loadingSessions,
    onSetDeadline,
    deadlineDays,
    savingDeadlineDays,
    onDeadlineDaysChange,
    onSaveDeadlineDays,
    deadlineEnabled,
    savedDeadlineEnabled,
    onDeadlineEnabledChange,
    savingDeadlineEnabled,
    deadlineSettingsDirty,
    onDiscardDeadlineSettings,
}) => {
    const [bulkDays, setBulkDays] = useState(7);
    const [onlyNew, setOnlyNew] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    const isNewlyEnabled = deadlineEnabled && !savedDeadlineEnabled;

    const affectedCount = onlyNew
        ? sessions.filter((s) => s && !s.reviewDeadline).length
        : sessions.length;
    const skippedCount = sessions.length - affectedCount;

    const handleApply = async () => {
        if (bulkDays < 1) return;
        const toUpdate = onlyNew ? sessions.filter((s) => s && !s.reviewDeadline) : sessions;
        if (toUpdate.length === 0) {
            alert("No activities to update. All activities already have deadlines set.");
            return;
        }
        setSaving(true);
        try {
            await onSetDeadline(toUpdate, bulkDays);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Header>
                <div>
                    <h2>Deadline Settings</h2>
                    <p>Configure the default assessment deadline for new activities.</p>
                </div>
            </Header>

            <EnableToggleCard
                title="Enable assessment deadlines"
                desc="When enabled, new activities will automatically receive an assessment deadline. You can also set deadlines in bulk below."
                enabled={deadlineEnabled}
                onChange={onDeadlineEnabledChange}
                disabled={savingDeadlineEnabled}
            />

            {deadlineEnabled && (
                <Card>
                    <Section>
                        <SectionLabelRow>
                            <SectionLabel>Default deadline for new activities</SectionLabel>
                            <InfoIconContainer
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                <InfoIcon>ⓘ</InfoIcon>
                                {showTooltip && (
                                    <Tooltip>
                                        Choose a default deadline window, then click <strong>Save changes</strong> to activate assessment deadlines.
                                    </Tooltip>
                                )}
                            </InfoIconContainer>
                        </SectionLabelRow>
                        <SectionDesc>
                            The assessment window for a new activity closes exactly {deadlineDays * 24} hours ({deadlineDays} {deadlineDays === 1 ? "day" : "days"}) after the student submits the activity, not at 11:59 PM on the last day. If the artifact is updated later, the window restarts from the time of the update.
                        </SectionDesc>
                        <DurationPicker
                            value={deadlineDays}
                            options={QUICK_DAY_OPTIONS}
                            onChange={onDeadlineDaysChange}
                        />
                        <PreviewBox>
                            <PreviewCalc>Deadline calculation: <strong>Activity submission time + {deadlineDays} {deadlineDays === 1 ? "day" : "days"} ({deadlineDays * 24} hours)</strong></PreviewCalc>
                            <PreviewExample>Example: Submitted on {fmtWithTime(EXAMPLE_SUBMIT_TIME)} → Closes on {exampleWindowClose(deadlineDays)}</PreviewExample>
                        </PreviewBox>
                    </Section>
                    <ButtonRow>
                        <SecondaryButton
                            type="button"
                            onClick={onDiscardDeadlineSettings}
                            disabled={!deadlineSettingsDirty || savingDeadlineDays}
                        >
                            Discard changes
                        </SecondaryButton>
                        <PrimaryButton
                            type="button"
                            onClick={() => onSaveDeadlineDays(deadlineDays)}
                            disabled={!deadlineSettingsDirty || savingDeadlineDays}
                        >
                            <FiClock size={16} />
                            {savingDeadlineDays ? "Saving…" : isNewlyEnabled ? "Enable & Save" : "Save changes"}
                        </PrimaryButton>
                    </ButtonRow>
                </Card>
            )}

            {deadlineEnabled && (
                <>
                    {!loadingSessions && sessions.length === 0 ? (
                        <EmptyState>
                            <EmptyIcon><FiCalendar size={32} /></EmptyIcon>
                            <EmptyTitle>No activities found</EmptyTitle>
                            <EmptyDesc>Once activities are added to this course, you can set assessment deadlines for all of them here.</EmptyDesc>
                        </EmptyState>
                    ) : (
                        <Card>
                            <Section>
                                <SectionLabel>Set Deadline for All Activities</SectionLabel>
                                <SectionDesc>
                                    Override deadlines on existing activities in bulk. Each activity's assessment window will close exactly {bulkDays * 24} hours ({bulkDays} {bulkDays === 1 ? "day" : "days"}) after the student submitted the activity, or after its artifact was last updated.
                                </SectionDesc>
                                <DurationPicker
                                    value={bulkDays}
                                    options={QUICK_DAY_OPTIONS}
                                    onChange={setBulkDays}
                                />
                                <PreviewBox>
                                    <PreviewCalc>Deadline calculation: <strong>Activity submission time + {bulkDays} {bulkDays === 1 ? "day" : "days"} ({bulkDays * 24} hours)</strong></PreviewCalc>
                                    <PreviewExample>Example: Submitted on {fmtWithTime(EXAMPLE_SUBMIT_TIME)} → Closes on {exampleWindowClose(bulkDays)}</PreviewExample>
                                </PreviewBox>
                            </Section>

                            <Divider />

                            <CheckRow>
                                <Checkbox
                                    type="checkbox"
                                    id="onlyNew"
                                    checked={onlyNew}
                                    onChange={(e) => setOnlyNew(e.target.checked)}
                                />
                                <CheckLabel htmlFor="onlyNew">
                                    Only apply to activities without an existing deadline
                                </CheckLabel>
                            </CheckRow>

                            {loadingSessions ? (
                                <SummaryText>Loading activities…</SummaryText>
                            ) : (
                                <SummaryText>
                                    <strong>{affectedCount}</strong> activit{affectedCount === 1 ? "y" : "ies"} will be updated
                                    {skippedCount > 0 && (
                                        <>, <strong>{skippedCount}</strong> skipped (already have a deadline)</>
                                    )}.
                                </SummaryText>
                            )}

                            <ButtonRow>
                                <PrimaryButton
                                    type="button"
                                    onClick={handleApply}
                                    disabled={saving || loadingSessions}
                                >
                                    <FiClock size={16} />
                                    {saving ? "Applying…" : "Set Deadline for All"}
                                </PrimaryButton>
                            </ButtonRow>
                        </Card>
                    )}
                </>
            )}
        </>
    );
};

export default DeadlineDefaultSettings;

const PreviewBox = styled.div`
    background: #eef2ff;
    border-radius: 10px;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PreviewCalc = styled.p`
    font-size: 13px;
    color: #3730a3;
    margin: 0;
`;

const PreviewExample = styled.p`
    font-size: 12px;
    color: #6366f1;
    font-style: italic;
    margin: 0;
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid #f3f4f6;
    margin: 0;
`;

const CheckRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Checkbox = styled.input`
    width: 16px;
    height: 16px;
    accent-color: #9333ea;
    cursor: pointer;
`;

const CheckLabel = styled.label`
    font-size: 14px;
    color: #374151;
    cursor: pointer;
`;

const SummaryText = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin: 0;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 56px 32px;
    background: white;
    border: 1px dashed #e5e7eb;
    border-radius: 14px;
    text-align: center;
`;

const EmptyIcon = styled.div`
    color: #d1d5db;
`;

const EmptyTitle = styled.p`
    font-size: 15px;
    font-weight: 700;
    color: #374151;
    margin: 0;
`;

const EmptyDesc = styled.p`
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
    max-width: 320px;
    line-height: 1.5;
`;

const SectionLabelRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 14px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const Tooltip = styled.div`
    position: absolute;
    top: 25px;
    left: -100px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 12px;
    width: 280px;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-size: 12px;
    line-height: 1.5;
    color: #374151;
`;
